import { FunctionComponent, useState, useEffect } from 'react';
import {
  Button,
  BankLogo,
  Icon,
  PaymentMethod,
  ELinxConnectionMethod,
  formattedCurrency,
  CardLogo,
  getContrastColor,
  InteracWordmark,
  PaymentMethodType,
  withStyles,
  MaterialSymbol,
  BodyText,
} from 'vopay';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import useStorage from '@/hooks/useStorage';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import usePostRequirePadAgreement from '@/hooks/useApi/usePostRequirePadAgreement';
import AgreePADDialogue from '../CreditCard/Dialogue/AgreePADDialogue';
import BankConnectionTermsAndConditions from '../BankAccount/BankConnectionTermsAndConditions/BankConnectionTermsAndConditions';
import PageTitle from '@/components/UI/PageTitle';
import styles from './PaymentMethodSummary.module.scss';
import { useLayout } from '@/providers/Layout.provider';
import ConvenienceFeeWithTooltip from './ConvenienceFeeWithTooltip';
import { useDataFetchIndicator } from '@/hooks';
import { useBank } from '@/providers/Bank.provider';

type LocationProp = {
  selectedPaymentMethod: PaymentMethod;
  token: string;
  amount: string | number;
};

const cn = withStyles(styles);

const PaymentMethodSummary: FunctionComponent = () => {
  const [, setPending] = useDataFetchIndicator();
  const history = useHistory();
  const storage = useStorage();
  const { t } = useTranslation('translations', { keyPrefix: 'PaymentMethod' });
  const { token } = useToken();
  const { state } = useLocation<LocationProp>();
  const { Token, flow, paylink } = usePaylink();
  const { color } = useLayout();
  const [isPadAgreementRequired, setIsPadAgreementRequired] = useState(false);
  const [showingTermsAndConditions, setShowingTermsAndConditions] = useState(false);
  const [showingPadDialog, setShowingPadDialog] = useState(false);
  const [, , , setPaylinkAccount] = usePostPaylinkSetAccount({});
  const { requirePadAgreement } = usePostRequirePadAgreement();
  const hasFees = state.amount ? parseFloat(state.amount.toString()) - parseFloat(paylink.Amount) !== 0 : false;
  const { BankAccountRail } = useBank();
  useEffect(() => {
    const requirePadAgreements = async () => {
      const bearerToken = token?.JWT || '';
      const res = await requirePadAgreement(
        {
          PaymentMethod: state.selectedPaymentMethod.PaymentMethod,
          Token: state.token,
        },
        bearerToken,
      );
      if (
        res.RequiresPADAgreement &&
        (flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED) &&
        (state.selectedPaymentMethod.PaymentMethod === 'BankAccount' ||
          state.selectedPaymentMethod.PaymentMethod === 'CreditCard')
      ) {
        setIsPadAgreementRequired(true);
      }
    };
    requirePadAgreements();
  }, [state]);

  // Map PaymentMethod to internal type
  const paymentMethodMap: Record<PaymentMethodType, ELinxConnectionMethod> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    Email: 'email',
    PhoneNumber: 'email',
    GooglePay: 'googlepay',
    ApplePay: 'applepay',
    Venmo: 'venmo',
    PayPal: 'paypal',
  };

  const interacTypes = ['Email', 'PhoneNumber'];

  const handleSetPaylinkAccount = async () => {
    try {
      setPending(true);
      const methodKey = state.selectedPaymentMethod.PaymentMethod;
      const storageType =
        methodKey === 'BankAccount'
          ? 'bank'
          : methodKey === 'CreditCard'
            ? 'credit'
            : methodKey === 'DebitCard'
              ? 'debitcard'
              : interacTypes.includes(methodKey)
                ? 'interac'
                : methodKey === 'GooglePay'
                  ? 'googlepay'
                  : 'applepay';

      storage.paymentMethod.storePaymentMethod(storageType);

      const mappedType = paymentMethodMap[methodKey] || 'applepay';

      const res = await setPaylinkAccount({
        SingleUseToken: Token,
        Iq11Token: state.token,
        PaymentMethod: mappedType,
        BankAccountRail: methodKey === 'BankAccount' ? BankAccountRail : undefined,
      });
      const isCard =
        state.selectedPaymentMethod.PaymentMethod === 'CreditCard' ||
        state.selectedPaymentMethod.PaymentMethod === 'DebitCard' ||
        state.selectedPaymentMethod.PaymentMethod === 'GooglePay';
      const isBank = state.selectedPaymentMethod.PaymentMethod === 'BankAccount';

      if (res.Success) {
        history.push('/thank-you', {
          country: state.selectedPaymentMethod.ExtraFields.CountryAbbreviation,
          province: state.selectedPaymentMethod.ExtraFields.ProvinceAbbreviation,
          paymentMethodType: mappedType,
          paymentMethod: isBank
            ? state.selectedPaymentMethod.PaymentDetails?.slice(-7)
            : isCard
              ? `****${state.selectedPaymentMethod.PaymentDetails?.replaceAll('-', '').slice(-4)}`
              : state.selectedPaymentMethod.PaymentDetails,
          isExistingConnection: true,
        });
      } else {
        history.push('/connect/transaction-error');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={cn('paymentMethodSummary')}>
      <BankConnectionTermsAndConditions
        showTermsAndConditions={showingTermsAndConditions}
        setShowTermsAndConditions={() => setShowingTermsAndConditions(false)}
        onNext={() => {
          handleSetPaylinkAccount();
          setShowingTermsAndConditions(false);
        }}
      />

      <AgreePADDialogue
        isOpen={showingPadDialog}
        onClose={setShowingPadDialog}
        onNext={() => {
          handleSetPaylinkAccount();
          setShowingPadDialog(false);
        }}
      />

      <PageTitle title={t('summary')} size="lg" bold />

      <div className={cn('summaryCard')}>
        <div className={cn('subCard')}>
          <h5 className={cn('summaryTitle')}>
            {flow <= Flow.WITHDRAW__MULTIPLE_PAYMENTS ? t('totalReceiving') : t('totalDue')}
          </h5>

          <div className={cn('summaryAmount')}>
            {paylink.Currency} <span>{formattedCurrency(state.amount.toString())}</span>
          </div>

          <ConvenienceFeeWithTooltip
            paymentMethodType={state.selectedPaymentMethod.PaymentMethod}
            country={state.selectedPaymentMethod.ExtraFields.CountryAbbreviation || undefined}
            province={state.selectedPaymentMethod.ExtraFields.ProvinceAbbreviation || undefined}
          />
        </div>

        <div className={cn('subCard')}>
          <h5 className={cn('summaryTitle')}>
            {flow <= Flow.WITHDRAW__MULTIPLE_PAYMENTS ? t('receivingTo') : t('payingFrom')}
          </h5>

          {state.selectedPaymentMethod.PaymentMethod === 'BankAccount' ? (
            <>
              {state.selectedPaymentMethod.ExtraFields.InstitutionName === 'VOPAY TESTING BANK' ? (
                <Icon icon="vLogo" width="64px" height="64px" />
              ) : (
                <BankLogo
                  width="64px"
                  height="64px"
                  financialInstitutionNumber={state.selectedPaymentMethod.ExtraFields.BankID || ''}
                />
              )}

              <div className={cn('paymentMethodInfo')}>
                <div className={cn('summaryBankName')}>
                  {state.selectedPaymentMethod.ExtraFields.InstitutionName === '-'
                    ? ''
                    : state.selectedPaymentMethod.ExtraFields.InstitutionName}
                </div>

                <div>{state.selectedPaymentMethod.PaymentDetails?.slice(-7)}</div>
              </div>
            </>
          ) : state.selectedPaymentMethod.PaymentMethod === 'CreditCard' ||
            state.selectedPaymentMethod.PaymentMethod === 'DebitCard' ||
            state.selectedPaymentMethod.PaymentMethod === 'GooglePay' ? (
            <>
              <CardLogo width="60px" height="60px" cardBrand={state.selectedPaymentMethod.ExtraFields.CardBrand} />

              <div className={cn('paymentMethodInfo')}>
                <div>
                  {state.selectedPaymentMethod.PaymentMethod === 'CreditCard'
                    ? t('credit')
                    : state.selectedPaymentMethod.PaymentMethod === 'DebitCard'
                      ? t('debitCard')
                      : 'Google Pay'}
                </div>

                <div>****{state.selectedPaymentMethod.PaymentDetails?.replaceAll('-', '').slice(-4)}</div>
              </div>
            </>
          ) : (
            <>
              <Icon icon="interac2" color="none" width="60px" height="60px" />

              <div className={cn('paymentMethodInfo')}>
                <InteracWordmark
                  type={flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED ? 'moneyRequest' : 'eTransfer'}
                />

                <div>{state.selectedPaymentMethod.PaymentDetails}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {hasFees && (
        <BodyText className={cn('refundWarning')}>
          <MaterialSymbol icon="info" size="24px" color="#2F82FFBF" />

          <div>
            <Trans
              t={t}
              i18nKey="refundWarning"
              components={{
                bold: <span style={{ fontWeight: 700 }} />,
              }}
              defaults="The fee for this payment method is <bold>non-refundable</bold>. To avoid this fee, choose a different payment method."
            />
          </div>
        </BodyText>
      )}

      <Button
        id="confirm-pay-button"
        label={flow <= Flow.WITHDRAW__MULTIPLE_PAYMENTS ? t('confirmButton') : t('confirmAndPayButton')}
        fontWeight={700}
        round
        textColor={getContrastColor(color)}
        background={color}
        onClick={() => {
          if (isPadAgreementRequired) {
            if (state.selectedPaymentMethod.PaymentMethod === 'BankAccount') {
              setShowingTermsAndConditions(true);
            } else {
              setShowingPadDialog(true);
            }
          } else {
            handleSetPaylinkAccount();
          }
        }}
      />
    </div>
  );
};

export default PaymentMethodSummary;
